// -----------------------------------------------------------------------------
// ScheduleReminderTaskSection
//
// Shared schedule-reminder + task checklist body, extracted verbatim from
// FollowUpV2's reminderNode block (Phase 1 of the reminder-component
// consolidation). Owns the V2 checklist hero (UpNextCardV2) + ChecklistMiniGrid,
// the inline ActionPanel, the reschedule banner, the pause/resume + unpause
// prompts, the RecentlyCompleted ack animation, the custom-reminder toggle, and
// the Pause dialog — along with every handler that drives them.
//
// All logic is relocated, NOT rewritten — the goal is zero behaviour change vs
// the inline FollowUpV2 block it replaced. Anything that used to call
// FollowUpV2's setSidebarTab/sidebar opener now calls the onOpenSidebarTab prop.
//
// Data source: useLeadReminderTasks (wrapping useV2ChecklistAutocomplete). The
// parent may pass its own single hook instance via the `checklist` prop so the
// section and the parent's chrome (badge / OutcomePanel counts) stay in sync off
// one source of truth; if omitted the section calls the hook itself (standalone
// reuse — Phase 2).
// -----------------------------------------------------------------------------
/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Stack, Typography, Button, IconButton, Dialog, DialogContent, DialogActions } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CRM_PRIMARY_BUTTON_SX } from 'src/styles/crmButtons';
import Iconify from 'src/components/Iconify';
import ScheduleTaskReminder from 'src/components/Attended/ScheduleTaskReminder';
import UpNextCardV2, { TaskAssigneeHeads } from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/v2/UpNextCardV2';
import RescheduleDialog from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/v2/RescheduleDialog';
import ChecklistMiniGrid from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/ChecklistMiniGrid';
import ActionPanel from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/ActionPanel';
import TasksEmptyStateCard from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/TasksEmptyStateCard';
import RecentlyCompletedCard from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/RecentlyCompletedCard';
import { callApi } from 'src/utils/apiCall';
import cascadeTaskDueDates from 'src/utils/cascadeTaskDueDates';
import useCustomSnackbar from 'src/hooks/useCustomSnackbar';
import { buildReopenedTaskCard, resolveTaskUndoTarget, TASK_UNDO_TARGET_TYPE } from 'src/utils/taskUndoTarget';
import { parseApiDate } from 'src/utils/taskDateUtils';
import { updateError } from '../../../redux/slices/user';
import { reopenChecklistItem, reopenTask, completeChecklistItem, completeTask, resumeLeadCascade } from '../../../redux/slices/taskSlice';
import { CHECKLIST_COMPLETION_STATE } from '../../../constants/tasksV2';
import { dispatch } from '../../../redux/store';
import useLeadReminderTasks from './hooks/useLeadReminderTasks';
import { ITEM_TYPE_TO_CHANNEL } from './standaloneTaskConstants';
import { getTaskRenamePlan } from './taskRenameRefreshUtils';
import { isInLeadTreatmentScope } from '../../../utils/taskPauseState';

// ── buildMiniGridGroups ───────────────────────────────────────────────────────
// Derives the { today, later, done } shape ChecklistMiniGrid expects from the
// flat items array the hook exposes. Relocated verbatim from FollowUpV2.
function buildMiniGridGroups(items) {
    const overdue = [];
    const today = [];
    const later = [];
    const done = [];
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

    items.forEach((item) => {
        if (item.completion_state !== 'OPEN' && item.completion_state !== 'QUEUED') {
            done.push(item);
            return;
        }
        const due = item.due_at ? new Date(parseApiDate(item.due_at)) : null;
        // Day-based: overdue = a PRIOR calendar day; today = due any time today
        // (or no due date). Matches the stats strip + UpNextCardV2 eyebrow.
        if (due && due < startOfToday) {
            overdue.push(item);
        } else if (!due || due <= endOfToday) {
            today.push(item);
        } else {
            later.push(item);
        }
    });

    return { overdue, today, later, done };
}

// eslint-disable-next-line react/prop-types
const NoopDbgTag = () => null;

export default function ScheduleReminderTaskSection({
    practiceId,
    leadId,
    leadDetail,
    /** LeadTreatment PK of the treatment this panel is operating in.
     *  Passed to resume-cascade so only this treatment's tasks are unpaused
     *  (treatment-scoped to avoid cross-treatment resume leak). */
    leadTreatmentId,
    onOpenSidebarTab,
    /** Arms/disarms the host's WhatsApp send->complete anchor. See FollowUpV2
     *  pendingWaTaskRef. Called with a task context on panel open and null on
     *  close/complete — ALWAYS set-or-clear, never conditionally skipped. */
    onArmWhatsappTask,
    onReloadLeads,
    // eslint-disable-next-line no-unused-vars
    onClose, // retained for host API compat; create no longer auto-closes (FIX C)
    checklist: checklistProp,
    // eslint-disable-next-line no-unused-vars
    renderHero, // reserved for Phase 2 layout variants
    // eslint-disable-next-line no-unused-vars
    compact, // reserved for Phase 2 dense layouts
    // eslint-disable-next-line no-unused-vars
    debugSource,
    dbgSx,
    DbgTag,
}) {
    const { enqueueSnackbar } = useCustomSnackbar();

    // Single source of truth: use the parent's hook instance when provided so the
    // parent chrome (badge / OutcomePanel) and this section never diverge. When
    // omitted (standalone reuse) call the hook ourselves.
    const ownChecklist = useLeadReminderTasks({ practiceId, leadId });
    const checklist = checklistProp || ownChecklist;
    const {
        items: rawItems, loading, refresh, activeTaskIds, activeTaskCount,
    } = checklist;

    // Scope the checklist to the active treatment so the attended/follow-up popup
    // doesn't show the OTHER treatment's tasks on multi-treatment leads.
    // Lead-level/null tasks must not make a selected treatment look paused; they
    // stay visible in the Tasks tab's separate "No treatment" bucket.
    const items = leadTreatmentId
        ? rawItems.filter((i) => isInLeadTreatmentScope(i, leadTreatmentId))
        : rawItems;

    // initialLoading latch — relocated verbatim from FollowUpV2.
    const hasLoadedOnceRef = useRef(false);
    if (!loading) hasLoadedOnceRef.current = true;
    const initialLoading = loading && !hasLoadedOnceRef.current;

    // ── Local state (relocated verbatim from FollowUpV2) ────────────────────────
    const [actionPanelItem, setActionPanelItem] = useState(null);
    const [featuredItem, setFeaturedItem] = useState(null);
    const [dueDateOverrides, setDueDateOverrides] = useState(() => new Map());
    const [showRescheduleBanner, setShowRescheduleBanner] = useState(false);
    const [bannerRescheduleOpen, setBannerRescheduleOpen] = useState(false);
    const [recentlyCompleted, setRecentlyCompleted] = useState(null);
    const recentlyCompletedTimerRef = useRef(null);
    const [pauseDialogItem, setPauseDialogItem] = useState(null);
    const [pauseLoading, setPauseLoading] = useState(false);
    const [resumeLoading, setResumeLoading] = useState(false);
    const [pauseUntilDate, setPauseUntilDate] = useState('');
    const [showUnpausePrompt, setShowUnpausePrompt] = useState(false);
    const [pausedCount, setPausedCount] = useState(0);
    const [unpauseLoading, setUnpauseLoading] = useState(false);
    const pauseInFlightRef = useRef(false);
    const [optimisticPausedInfo, setOptimisticPausedInfo] = useState(null);
    const [showCustomReminderToggle, setShowCustomReminderToggle] = useState(false);

    const completingItemIdsRef = useRef(new Set());

    const currentLeadId = leadId;
    const leadEmail = leadDetail?.lead_contact?.email || '';

    const applyDueDateOverrideEarly = (it) => {
        if (!it || !dueDateOverrides.has(it.taskId)) return it;
        const d = dueDateOverrides.get(it.taskId);
        // Guard against an invalid Date in the override map — toISOString() throws
        // "Invalid time value" (RangeError) and white-screens the lead detail
        // (Sentry JAVASCRIPT-REACT-RF). Fall back to the task's existing due_at.
        if (!(d instanceof Date) || Number.isNaN(d.getTime())) return it;
        return { ...it, due_at: d.toISOString() };
    };

    // Pause-state detection — relocated from FollowUpV2.
    // "All paused" = every non-resolved item is paused (no OPEN/QUEUED remain).
    const allDoneItems = items.filter((i) => i.completion_state !== 'OPEN' && i.completion_state !== 'QUEUED');
    const allPausedFromServer = items.length > 0
        && allDoneItems.length === items.length
        && allDoneItems.some((i) => i.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED);
    const allPaused = !!optimisticPausedInfo || allPausedFromServer;

    // FIX A: "all-complete" card only shows when EVERY item is genuinely resolved
    // (YES / COMPLETE / SKIPPED). Any item still OPEN, QUEUED, or PAUSED keeps it false.
    const RESOLVED = new Set(['YES', 'COMPLETE', 'SKIPPED']);
    const allTasksComplete = items.length > 0
        && items.every((i) => RESOLVED.has(i.completion_state))
        && !allPaused;

    // FIX B: count of currently-paused items, used to surface an inline "Unpause"
    // affordance when SOME (not all) tasks are paused (mixed open + paused state).
    const pausedItemsCount = items.filter(
        (i) => i.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED
    ).length;

    // Broadcast a task change so the Overview next-step card + Tasks badge refresh.
    const broadcastTasksChanged = (source) => window.dispatchEvent(new CustomEvent('lead-tasks-v2-changed', { detail: { source } }));

    // ── Mark complete — immediate-persist ───────────────────────────────────────
    const handleCardMarkComplete = useCallback((item) => {
        if (!item?.taskId) return;
        // Disarm ONLY when the item being completed is the one whose panel is open.
        // This handler also serves the mini-grid, which stays visible alongside an
        // open ActionPanel — an unconditional disarm here would drop the anchor
        // when a rep ticks an UNRELATED item while the WhatsApp panel is open,
        // re-creating the exact bug this PR fixes.
        if (onArmWhatsappTask && actionPanelItem && item.id === actionPanelItem.id) {
            onArmWhatsappTask(null);
        }

        if (item.isStandaloneTask) {
            const SEND_LABELS = {
                WHATSAPP_SENT: 'WhatsApp sent', EMAIL_SENT: 'Email sent',
                LOG_PHONE_CALL: 'Call logged', LEAD_NEXT_ACTION: 'Next action set',
            };
            const label = (item.item_type && SEND_LABELS[item.item_type]) || 'Task completed';
            dispatch(completeTask(practiceId, item.taskId, ({ success }) => {
                if (success) {
                    if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
                    setRecentlyCompleted({ completed: item, label, completedAt: new Date(), fromSend: false });
                    recentlyCompletedTimerRef.current = setTimeout(() => setRecentlyCompleted(null), 4000);
                    const remaining = items.filter((i) => i.completion_state === 'OPEN' && i.id !== item.id);
                    setFeaturedItem(remaining[0] || null);
                    setShowRescheduleBanner(true);
                    setBannerRescheduleOpen(false);
                    const paused = items.filter((i) => i.completion_state === 'PAUSED' && i.id !== item.id);
                    if (paused.length > 0) {
                        setPausedCount(paused.length);
                        setShowUnpausePrompt(true);
                    }
                    refresh({ bustCache: true });
                    broadcastTasksChanged('task-complete');
                }
            }));
            return;
        }

        if (!item?.itemId) return;
        const inFlightKey = `${item.taskId}:${item.itemId}`;
        if (completingItemIdsRef.current.has(inFlightKey)) return;
        completingItemIdsRef.current.add(inFlightKey);

        const payload = item.resolvedAction ? { resolved_action: item.resolvedAction } : null;
        dispatch(completeChecklistItem(
            practiceId, item.taskId, item.itemId,
            ({ success }) => {
                completingItemIdsRef.current.delete(inFlightKey);
                if (success) {
                    const COMPLETION_LABELS = {
                        WHATSAPP_SENT: 'WhatsApp sent',
                        EMAIL_SENT: 'Email sent',
                        LOG_PHONE_CALL: 'Call logged',
                        LEAD_NEXT_ACTION: 'Next action set',
                    };
                    const label = COMPLETION_LABELS[item.item_type] || 'Task completed';
                    if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
                    setRecentlyCompleted({ completed: item, label, completedAt: new Date(), fromSend: false });
                    recentlyCompletedTimerRef.current = setTimeout(() => setRecentlyCompleted(null), 4000);
                    const remaining = items.filter((i) => i.completion_state === 'OPEN' && i.id !== item.id);
                    setFeaturedItem(remaining[0] || null);
                    setShowRescheduleBanner(true);
                    const paused = items.filter((i) => i.completion_state === 'PAUSED' && i.id !== item.id);
                    if (paused.length > 0) {
                        setPausedCount(paused.length);
                        setShowUnpausePrompt(true);
                    }
                    refresh({ bustCache: true });
                }
            },
            payload,
            CHECKLIST_COMPLETION_STATE.YES,
        ));
    }, [practiceId, items, refresh, onArmWhatsappTask, actionPanelItem]);

    const handleCardUndo = useCallback((item) => {
        const undoTarget = resolveTaskUndoTarget(item);
        if (!undoTarget) {
            dispatch(updateError({ success: false, message: "Couldn't undo this task. Please refresh and try again." }));
            return;
        }

        const restoreCard = () => {
            if (onArmWhatsappTask) onArmWhatsappTask(null);
            setFeaturedItem(buildReopenedTaskCard(item, undoTarget));
            setShowRescheduleBanner(false);
            setBannerRescheduleOpen(false);
            setActionPanelItem(null);
            refresh({ bustCache: true });
            broadcastTasksChanged('task-reopen');
        };

        if (undoTarget.type === TASK_UNDO_TARGET_TYPE.CHECKLIST_ITEM) {
            dispatch(reopenChecklistItem(
                practiceId, undoTarget.taskId, undoTarget.itemId,
                ({ success }) => { if (success) restoreCard(); }
            ));
            return;
        }

        dispatch(reopenTask(
            practiceId, undoTarget.taskId,
            ({ success }) => { if (success) restoreCard(); }
        ));
    }, [practiceId, refresh, onArmWhatsappTask]);

    const handleAckUndo = useCallback(() => {
        if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
        if (recentlyCompleted?.completed) handleCardUndo(recentlyCompleted.completed);
        setRecentlyCompleted(null);
    }, [recentlyCompleted, handleCardUndo]);

    // ── upNextItem / effectiveUpNextItem / miniGridItems ───────────────────────
    const openCount = items.filter((i) => i.completion_state === 'OPEN').length;
    const upNextItemRaw = items.find((i) => i.completion_state === 'OPEN') || null;
    const upNextItem = applyDueDateOverrideEarly(upNextItemRaw);
    const effectiveUpNextItem = applyDueDateOverrideEarly(featuredItem) || upNextItem;

    const miniGridItems = (effectiveUpNextItem
        ? items.filter((i) => i.id !== effectiveUpNextItem.id)
        : items
    ).map(applyDueDateOverrideEarly);
    const miniGridGroups = buildMiniGridGroups(miniGridItems);

    const handleItemAction = useCallback((item) => {
        if (!item) return;
        if (item.id !== (featuredItem || upNextItem)?.id) {
            if (onOpenSidebarTab) onOpenSidebarTab('overview');
            setActionPanelItem(null);
            // Panel torn down by switching cards — disarm, or a later send would
            // complete the task whose panel is no longer open.
            if (onArmWhatsappTask) onArmWhatsappTask(null);
        }
        setFeaturedItem(item);
        setShowRescheduleBanner(false);
    }, [featuredItem, upNextItem, onOpenSidebarTab, onArmWhatsappTask]);

    const handleActionClick = useCallback((item) => {
        if (!item) return;
        // Arm the host's WhatsApp send->complete anchor. Forward the ORIGINAL item:
        // the standalone branch below rewrites item_type to LEAD_NEXT_ACTION for the
        // panel, and resolveWhatsappArmContext must see the real type. Called on
        // EVERY action open so a non-WhatsApp task disarms a previous WhatsApp one.
        if (onArmWhatsappTask) onArmWhatsappTask(item);
        if (item.isStandaloneTask) {
            setActionPanelItem({
                ...item,
                item_type: 'LEAD_NEXT_ACTION',
                initialChannel: ITEM_TYPE_TO_CHANNEL[item.item_type] || null,
            });
            return;
        }
        setActionPanelItem(item);
    }, [onOpenSidebarTab, onArmWhatsappTask]);

    // Opens the FollowUpPanel (Call / WA / Email / Note) for the no-task empty state.
    // Uses a virtual item (taskId/itemId null) so handlePanelComplete takes the safe
    // refresh() path — no checklist item endpoint is called, no 404 risk.
    const handleContactLead = useCallback(() => {
        // Virtual "Contact lead" panel — not a task, so nothing may stay armed.
        if (onArmWhatsappTask) onArmWhatsappTask(null);
        setActionPanelItem({
            item_type: 'LEAD_NEXT_ACTION',
            title: 'Contact lead',
            taskId: null,
            itemId: null,
            id: 'virtual-contact-lead',
            isVirtualItem: true,
            // treatment-scoped: passed through to FollowUpPanel sub-panels via item prop
            leadTreatmentId,
        });
    }, [leadTreatmentId, onArmWhatsappTask]);

    const handlePanelClose = useCallback(() => {
        setActionPanelItem(null);
        setFeaturedItem(null);
        // Panel closed without sending — disarm so a later unrelated send in the
        // sidebar WhatsApp tab cannot complete this task.
        if (onArmWhatsappTask) onArmWhatsappTask(null);
    }, [onArmWhatsappTask]);

    const handlePanelComplete = useCallback((completedItem) => {
        setActionPanelItem(null);
        setFeaturedItem(null);
        // Completed via the panel's own button — disarm so the send-anchor cannot
        // double-complete the same task if a message is sent afterwards.
        if (onArmWhatsappTask) onArmWhatsappTask(null);

        if (completedItem?.isStandaloneTask) {
            if (!completedItem.taskId) { refresh({ bustCache: true }); return; }
            const paused = items.filter((i) => i.completion_state === 'PAUSED' && i.id !== completedItem.id);
            const SEND_LABELS = {
                WHATSAPP_SENT: 'WhatsApp sent', EMAIL_SENT: 'Email sent',
                LOG_PHONE_CALL: 'Call logged', LEAD_NEXT_ACTION: 'Next action set',
            };
            const label = (completedItem.item_type && SEND_LABELS[completedItem.item_type]) || 'Task completed';
            dispatch(completeTask(practiceId, completedItem.taskId, ({ success }) => {
                if (success) {
                    if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
                    setRecentlyCompleted({ completed: completedItem, label, completedAt: new Date(), fromSend: false });
                    recentlyCompletedTimerRef.current = setTimeout(() => setRecentlyCompleted(null), 4000);
                    const remaining = items.filter((i) => i.completion_state === 'OPEN' && i.id !== completedItem.id);
                    setFeaturedItem(remaining[0] || null);
                    setShowRescheduleBanner(true);
                    setBannerRescheduleOpen(false);
                    if (paused.length > 0) {
                        setPausedCount(paused.length);
                        setShowUnpausePrompt(true);
                    }
                    refresh({ bustCache: true });
                    broadcastTasksChanged('task-complete');
                }
            }));
            return;
        }

        if (!completedItem?.taskId || !completedItem?.itemId) {
            refresh({ bustCache: true });
            return;
        }
        if (completedItem.completionAlreadyPersisted) {
            const SEND_LABELS = {
                WHATSAPP_SENT: 'WhatsApp sent', EMAIL_SENT: 'Email sent',
                LOG_PHONE_CALL: 'Call logged', LEAD_NEXT_ACTION: 'Next action set',
            };
            const label = (completedItem.item_type && SEND_LABELS[completedItem.item_type]) || 'Task completed';
            if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
            setRecentlyCompleted({ completed: completedItem, label, completedAt: new Date(), fromSend: false });
            recentlyCompletedTimerRef.current = setTimeout(() => setRecentlyCompleted(null), 4000);
            const remaining = items.filter((i) => i.completion_state === 'OPEN' && i.id !== completedItem.id);
            setFeaturedItem(remaining[0] || null);
            setShowRescheduleBanner(true);
            setBannerRescheduleOpen(false);
            refresh({ bustCache: true });
            broadcastTasksChanged('task-complete');
            return;
        }
        const inFlightKey = `${completedItem.taskId}:${completedItem.itemId}`;
        if (completingItemIdsRef.current.has(inFlightKey)) return;
        completingItemIdsRef.current.add(inFlightKey);

        const payload = completedItem.resolvedAction
            ? { resolved_action: completedItem.resolvedAction }
            : null;

        dispatch(completeChecklistItem(
            practiceId, completedItem.taskId, completedItem.itemId,
            ({ success }) => {
                completingItemIdsRef.current.delete(inFlightKey);
                if (success) {
                    const SEND_LABELS = {
                        WHATSAPP_SENT: 'WhatsApp sent', EMAIL_SENT: 'Email sent',
                        LOG_PHONE_CALL: 'Call logged', LEAD_NEXT_ACTION: 'Next action set',
                    };
                    const label = (completedItem.item_type && SEND_LABELS[completedItem.item_type]) || 'Task completed';
                    if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
                    setRecentlyCompleted({ completed: completedItem, label, completedAt: new Date(), fromSend: false });
                    recentlyCompletedTimerRef.current = setTimeout(() => setRecentlyCompleted(null), 4000);
                    const remaining = items.filter((i) => i.completion_state === 'OPEN' && i.id !== completedItem.id);
                    setFeaturedItem(remaining[0] || null);
                    setShowRescheduleBanner(true);
                    setBannerRescheduleOpen(false);
                    refresh({ bustCache: true });
                }
            },
            payload,
            CHECKLIST_COMPLETION_STATE.YES,
        ));
    }, [practiceId, items, refresh]);

    // R-5: clear recentlyCompleted timer on unmount — relocated verbatim from FollowUpV2.
    useEffect(() => () => clearTimeout(recentlyCompletedTimerRef.current), []);

    const handleChangeDueDate = useCallback(async (item, newDate, newTitle) => {
        // Guard a cleared/invalid date — RescheduleDialog can hand back null/Invalid Date
        // when the user empties the field; toISOString() on it throws and white-screens.
        let parsedNewDate = null;
        if (newDate instanceof Date) { parsedNewDate = newDate; }
        else if (newDate) { parsedNewDate = new Date(newDate); }
        const {
            dueDateChanged,
            isChecklistItem,
            isStandalone,
            titleChanged,
            title: nextTitle,
        } = getTaskRenamePlan(item, parsedNewDate, newTitle);
        if ((!parsedNewDate || Number.isNaN(parsedNewDate.getTime())) && !titleChanged) {
            dispatch(updateError({ success: false, message: 'Please pick a valid date and time.' }));
            return false;
        }
        if (!dueDateChanged && !titleChanged) return true;

        if (!item?.taskId) {
            if (!item?._templateId) {
                dispatch(updateError({ success: false, message: "Due dates can't be set on this item yet." }));
                return false;
            }
            const spawnResp = await callApi(
                `/api/v1/practices/${practiceId}/tasks/from-template/`,
                'post',
                { template_id: item._templateId, lead_id: currentLeadId }
            );
            if (!spawnResp.ok && spawnResp.status !== 200 && spawnResp.status !== 201) {
                dispatch(updateError({ success: false, message: "Couldn't create tasks. Please try again." }));
                return false;
            }
            const newTaskId = spawnResp.data?.id;
            if (newTaskId) {
                await callApi(`/api/v1/practices/${practiceId}/tasks/${newTaskId}/`, 'patch', {
                    due_at: parsedNewDate.toISOString(),
                    ...(isStandalone && titleChanged ? { title: nextTitle } : {}),
                });
                enqueueSnackbar('Tasks created and due date set.', { variant: 'success' });
            } else {
                dispatch(updateError({ success: true, message: 'Tasks created! Select a task to set its due date.' }));
            }
            refresh({ bustCache: true });
            return true;
        }

        setFeaturedItem(item);
        setShowRescheduleBanner(false);

        const cascade = dueDateChanged
            ? cascadeTaskDueDates({
                items,
                anchorTaskId: item.taskId,
                oldDueAt: item.due_at,
                newDueAt: parsedNewDate,
            })
            : [];

        const prevOverrides = new Map(dueDateOverrides);
        const nextOverrides = new Map(dueDateOverrides);
        // Only store VALID dates — an invalid newDueAt (e.g. a task with an
        // unparseable due_at) must never reach applyDueDateOverrideEarly's
        // toISOString(). Mirrors the PATCH filter below.
        cascade.forEach(({ taskId, newDueAt: d }) => {
            if (d instanceof Date && !Number.isNaN(d.getTime())) nextOverrides.set(taskId, d);
        });
        setDueDateOverrides(nextOverrides);

        // 1. Cascade due_at across all related tasks (anchor + follow-ups).
        //    For checklist items the title rename goes to a dedicated /checklist/ PATCH
        //    below, so we deliberately omit title here to avoid task.title vs item.title
        //    mismatch.  For standalone tasks the title belongs on the task PATCH (current
        //    behaviour, unchanged).
        let taskPatchRequests = [];
        if (dueDateChanged) {
            taskPatchRequests = cascade
                .filter(({ newDueAt: d }) => d instanceof Date && !Number.isNaN(d.getTime()))
                .map(({ taskId, newDueAt: d }) => ({
                    taskId,
                    payload: {
                        due_at: d.toISOString(),
                        ...(taskId === item.taskId ? {
                            is_anchor: true,
                            // Standalone ad-hoc task: rename in the same PATCH as the due-date.
                            // Checklist item: title goes to the /checklist/{itemId}/ PATCH below.
                            // Template-backed task (no itemId, no isStandaloneTask): NO title sent —
                            // backend rejects rename on template tasks (DENTALSEM-CRM-1E0Y).
                            ...(isStandalone && titleChanged ? { title: nextTitle } : {}),
                        } : {}),
                    },
                }));
        } else if (isStandalone && titleChanged) {
            taskPatchRequests = [{ taskId: item.taskId, payload: { title: nextTitle } }];
        }

        const dueDateResults = await Promise.allSettled(
            taskPatchRequests.map(({ taskId, payload }) =>
                callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'patch', payload)
            )
        );

        const anyFailed = dueDateResults.some((r) => r.status === 'rejected' || !r.value?.ok);
        if (anyFailed) {
            setDueDateOverrides(prevOverrides);
            dispatch(updateError({ success: false, message: "Couldn't update due dates. Please try again." }));
            return false;
        }

        // 2. Checklist-item rename: PATCH /tasks/{taskId}/checklist/{itemId}/ { title }.
        //    checklist_items[].title is the display source for popup cards; patching the
        //    parent task title has no effect on what the card shows.
        //    Only send when title actually changed (non-empty + different from current).
        if (isChecklistItem && titleChanged) {
            const titleResult = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${item.taskId}/checklist/${item.itemId}/`,
                'patch',
                { title: nextTitle }
            );
            if (!titleResult?.ok) {
                // Due dates are already saved — partial success: inform the user.
                dispatch(updateError({ success: false, message: "Due date updated but couldn't rename the task. Please try again." }));
                refresh({ bustCache: true });
                broadcastTasksChanged('reschedule');
                return false;
            }
            setFeaturedItem((current) => {
                const target = current && current.id === item.id ? current : item;
                return { ...target, title: nextTitle, name: nextTitle };
            });
        }
        if (isStandalone && titleChanged) {
            setFeaturedItem((current) => {
                const target = current && current.id === item.id ? current : item;
                return { ...target, title: nextTitle, name: nextTitle };
            });
        }

        // 3. Feedback — standardised messages matching the action taken.
        let successMessage = 'Task rescheduled';
        if (titleChanged && dueDateChanged) {
            successMessage = 'Task renamed and rescheduled';
        } else if (titleChanged) {
            successMessage = 'Task renamed';
        }
        dispatch(updateError({ success: true, message: successMessage }));

        // 4. Refresh all surfaces.  refresh() re-fetches /task-checklists/ which
        //    includes checklist_items[].title so the updated title is read back
        //    immediately (a tasks-list-only refetch would miss it).
        refresh({ bustCache: true });
        // Propagate a reschedule/rename to EVERY surface (Tasks tab, Overview
        // next-step card, header badge) so a renamed task shows its new title
        // everywhere — not just inside this popup. Sourced → badge-only, no flicker.
        broadcastTasksChanged('reschedule');
        return true;
    }, [practiceId, items, dueDateOverrides, refresh]);

    const handleSkip = useCallback((skippedItem) => {
        if (!skippedItem?.taskId || !skippedItem?.itemId) return;
        setActionPanelItem(null);
        // Staff decided this step is not applicable. Disarm, or a later send would
        // overwrite SKIPPED with YES — the same staff-decision invariant the backend
        // protects in TaskService.complete_task's cascade.
        if (onArmWhatsappTask) onArmWhatsappTask(null);
        const remaining = items.filter(
            (i) => i.completion_state === 'OPEN' && i.id !== skippedItem.id
        );
        dispatch(completeChecklistItem(
            practiceId, skippedItem.taskId, skippedItem.itemId,
            ({ success }) => {
                if (success) {
                    setFeaturedItem(remaining[0] || null);
                    refresh({ bustCache: true });
                }
            },
            { source: 'upnext_skip' },
            CHECKLIST_COMPLETION_STATE.SKIPPED,
        ));
    }, [items, practiceId, refresh, onArmWhatsappTask]);

    const handlePause = useCallback((item) => {
        setActionPanelItem(null);
        // Same invariant as handleSkip: PAUSED must not be overwritten by a send.
        if (onArmWhatsappTask) onArmWhatsappTask(null);
        pauseInFlightRef.current = false;
        const d = new Date();
        d.setDate(d.getDate() + 7);
        const pad = (n) => String(n).padStart(2, '0');
        const localStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
        setPauseUntilDate(localStr);
        setPauseDialogItem(item);
    }, [onArmWhatsappTask]);

    const handlePauseConfirm = useCallback(async () => {
        const item = pauseDialogItem;
        if (!item?.taskId || !item?.itemId || !pauseUntilDate) return;
        if (pauseInFlightRef.current) return;
        pauseInFlightRef.current = true;
        setPauseLoading(true);
        const resp = await callApi(
            `/api/v1/practices/${practiceId}/tasks/${item.taskId}/checklist/${item.itemId}/pause/`,
            'post',
            { paused_until: new Date(pauseUntilDate).toISOString() }
        );
        setPauseLoading(false);
        if (resp.ok) {
            setOptimisticPausedInfo({
                paused_until: new Date(pauseUntilDate).toISOString(),
                title: item.title || item.taskTitle || item.name || null,
            });
            setFeaturedItem(null);
            setPauseDialogItem(null);
            dispatch(updateError({ success: true, message: 'Task paused' }));
            refresh({ bustCache: true });
            broadcastTasksChanged('pause');
        } else {
            pauseInFlightRef.current = false;
            dispatch(updateError({ success: false, message: "Couldn't pause. Please try again." }));
        }
    }, [pauseDialogItem, pauseUntilDate, practiceId, refresh]);

    const handleResumeChecklist = useCallback(() => {
        const firstPaused = items.find((i) => i.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED);
        if (!firstPaused?.taskId || !firstPaused?.itemId) return;
        setOptimisticPausedInfo(null);
        setResumeLoading(true);
        dispatch(reopenChecklistItem(
            practiceId, firstPaused.taskId, firstPaused.itemId,
            ({ success }) => {
                setResumeLoading(false);
                if (success) {
                    setFeaturedItem(null);
                    refresh({ bustCache: true });
                    broadcastTasksChanged('resume-checklist');
                }
            }
        ));
    }, [items, practiceId, refresh]);

    // ── Stats strip data ────────────────────────────────────────────────────────
    const nowForStats = new Date();
    const startOfTodayForStats = new Date(nowForStats.getFullYear(), nowForStats.getMonth(), nowForStats.getDate(), 0, 0, 0, 0);
    const endOfTodayForStats = new Date(nowForStats.getFullYear(), nowForStats.getMonth(), nowForStats.getDate(), 23, 59, 59);
    const statsDoneCount = items.filter((i) => i.completion_state !== 'OPEN').length;
    // Day-based: OVERDUE only when due on a PRIOR calendar day. A task due any
    // time today counts as "Due today" (matches UpNextCardV2.getEyebrow).
    const statsOverdueCount = items.filter((i) => i.completion_state === 'OPEN' && i.due_at && new Date(parseApiDate(i.due_at)) < startOfTodayForStats).length;
    const statsTodayCount = items.filter((i) => i.completion_state === 'OPEN' && (!i.due_at || (new Date(parseApiDate(i.due_at)) >= startOfTodayForStats && new Date(parseApiDate(i.due_at)) <= endOfTodayForStats))).length;
    const statsLaterCount = items.filter((i) => i.completion_state === 'OPEN' && i.due_at && new Date(parseApiDate(i.due_at)) > endOfTodayForStats).length;
    const statsTotalCount = items.length;

    // ── Pause-state card data ─────────────────────────────────────────────────
    const firstPausedItem = items.find((i) => i.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED);
    const rawPausedUntil = firstPausedItem?.paused_until || optimisticPausedInfo?.paused_until || null;
    const pausedMoment = rawPausedUntil
        ? moment(rawPausedUntil, ['DD-MM-YYYY HH:mm:ss.SSSSSSZ', 'DD-MM-YYYY HH:mm:ss.SSSSSS[Z]', 'DD-MM-YYYY HH:mm:ss', 'DD-MM-YYYY', moment.ISO_8601])
        : null;
    const pausedUntilLabel = pausedMoment && pausedMoment.isValid()
        ? pausedMoment.format('dddd D MMMM')
        : null;
    const pausedOnTitle = firstPausedItem?.title || firstPausedItem?.taskTitle || optimisticPausedInfo?.title || null;
    const pausedCompletedCount = items.filter(
        (i) => i.completion_state !== CHECKLIST_COMPLETION_STATE.PAUSED && i.completion_state !== 'OPEN'
    ).length;

    const sectionDbgSx = dbgSx || (() => null);
    const SectionDbgTag = DbgTag || NoopDbgTag;

    return (
        <>
            <Box sx={{ ...sectionDbgSx('#00BFA5') }}>
                <SectionDbgTag color="#00BFA5" label="CHECKLIST" />

                {allPaused && (
                    <Box
                        data-testid="checklist-paused-card"
                        sx={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            py: 4, px: 3,
                            bgcolor: 'rgba(255,171,0,0.06)',
                            borderRadius: '16px',
                            border: '1px solid rgba(255,171,0,0.20)',
                            textAlign: 'center',
                        }}
                    >
                        <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: 'rgba(255,171,0,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                            <Iconify icon="mdi:pause-circle" width={28} height={28} sx={{ color: '#FFAB00' }} />
                        </Box>
                        <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em', mb: 0.5 }}>
                            {pausedUntilLabel ? `Paused until ${pausedUntilLabel}` : 'Checklist paused'}
                        </Typography>
                        {pausedOnTitle && (
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.01em', mb: 0.5 }}>
                                Next up: {pausedOnTitle}
                            </Typography>
                        )}
                        <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em', mb: 2.5, maxWidth: 280 }}>
                            {pausedCompletedCount > 0
                                ? `${pausedCompletedCount} step${pausedCompletedCount !== 1 ? 's' : ''} already done — remaining steps will resume from here.`
                                : 'Remaining steps are snoozed and will resume from here.'}
                        </Typography>
                        <LoadingButton
                            data-testid="checklist-resume-btn"
                            variant="contained"
                            disableElevation
                            loading={resumeLoading}
                            loadingPosition="start"
                            startIcon={<Iconify icon="mdi:play-circle" width={18} height={18} />}
                            onClick={handleResumeChecklist}
                            sx={{ ...CRM_PRIMARY_BUTTON_SX, fontSize: '14px', fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'none', borderRadius: '6px', px: 3, py: 0.875 }}
                        >
                            Resume checklist
                        </LoadingButton>
                    </Box>
                )}

                {/* Inline unpause when some tasks are paused. The "Checklist — N open"
                    header was removed as redundant with the Overdue / Due today / Later
                    breakdown strip below (they sum to the same open count). */}
                {!initialLoading && !allPaused && statsTotalCount > 0 && pausedItemsCount > 0 && openCount > 0 && (
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ mb: 1 }}>
                            <Button
                                data-testid="checklist-unpause-inline-btn"
                                size="small"
                                disableElevation
                                disabled={resumeLoading}
                                onClick={handleResumeChecklist}
                                startIcon={<Iconify icon="mdi:play-circle-outline" width={15} height={15} />}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    letterSpacing: '-0.005em',
                                    color: '#B76E00',
                                    bgcolor: 'rgba(255,171,0,0.10)',
                                    border: '1px solid rgba(255,171,0,0.30)',
                                    borderRadius: '999px',
                                    px: 1.25,
                                    py: 0.25,
                                    minWidth: 0,
                                    '&:hover': { bgcolor: 'rgba(255,171,0,0.18)', borderColor: 'rgba(255,171,0,0.45)' },
                                }}
                            >
                                {`${pausedItemsCount} task${pausedItemsCount !== 1 ? 's' : ''} paused — Unpause`}
                            </Button>
                    </Stack>
                )}

                {!initialLoading && !allPaused && statsTotalCount > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 28, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            {statsOverdueCount > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#FF5630', flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#FF5630', letterSpacing: '-0.005em' }}>
                                        {statsOverdueCount} Overdue
                                    </Typography>
                                </Box>
                            )}
                            {statsTodayCount > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#FF9500', flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                        {statsTodayCount} Due today
                                    </Typography>
                                </Box>
                            )}
                            {statsLaterCount > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6e6e73', flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                        {statsLaterCount} Later
                                    </Typography>
                                </Box>
                            )}
                            {statsDoneCount > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#34c759', flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                        {statsDoneCount} Done
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ flex: 1, maxWidth: 320, ml: 1.5, height: 4, borderRadius: '2px', bgcolor: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                            <Box
                                data-testid="progress-bar-fill"
                                sx={{
                                    height: '100%',
                                    width: `${statsTotalCount > 0 ? (statsDoneCount / statsTotalCount) * 100 : 0}%`,
                                    bgcolor: '#34c759',
                                    borderRadius: '2px',
                                    transition: 'width 400ms ease',
                                }}
                            />
                        </Box>
                    </Box>
                )}

                {showCustomReminderToggle && (
                    <Box sx={{ mt: 2 }}>
                        {/* Header (icon + title) lives INSIDE the ScheduleTaskReminder
                            card below — here we only keep the dismiss control. */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                            <Button
                                variant="text"
                                size="small"
                                onClick={() => setShowCustomReminderToggle(false)}
                                startIcon={<Iconify icon="eva:close-fill" width={16} height={16} />}
                                sx={{ color: '#919EAB', textTransform: 'none', fontSize: '12px', minWidth: 0 }}
                            >
                                Remove
                            </Button>
                        </Box>
                        <ScheduleTaskReminder
                            practiceId={practiceId}
                            leadId={currentLeadId}
                            leadDetail={leadDetail}
                            // FIX C: after a successful create, stay in the popup —
                            // dismiss the create form + refresh the task list, do NOT call onClose.
                            handleClose={() => {
                                setShowCustomReminderToggle(false);
                                refresh?.({ bustCache: true });
                                broadcastTasksChanged('custom-reminder-add');
                            }}
                            handleReloadLeads={onReloadLeads}
                            activeTaskCount={activeTaskCount}
                            activeTaskIds={activeTaskIds}
                            leadTreatmentId={leadTreatmentId}
                        />
                    </Box>
                )}

                {!showCustomReminderToggle && (
                <Box sx={{
                    display: 'flex', alignItems: 'stretch', gap: 2,
                    '& > [data-testid="action-panel"]': { flex: '0 0 70%', height: '100%', display: 'flex', flexDirection: 'column' },
                    '@media (max-width: 900px)': {
                        '& > [data-testid="action-panel"]': { flex: '0 0 100%' },
                    },
                }}>
                    <Box sx={{
                        flex: actionPanelItem ? '1 1 0' : '1',
                        minWidth: 0,
                        alignSelf: 'stretch',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'flex 320ms cubic-bezier(0.32, 0.72, 0, 1)',
                        ...(actionPanelItem ? { '& [data-testid="upnext-card-v2"]': { height: '100%' } } : {}),
                    }}>
                        {!recentlyCompleted && showRescheduleBanner && effectiveUpNextItem && !actionPanelItem && (
                            <Box
                                data-testid="reschedule-banner"
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
                                    px: 2, py: 1.25,
                                    bgcolor: 'rgba(7,141,238,0.06)',
                                    border: '1px solid rgba(7,141,238,0.18)',
                                    borderRadius: '10px',
                                    flexWrap: 'wrap', rowGap: '8px',
                                }}
                            >
                                <Iconify icon="mdi:calendar-clock" width={16} height={16} sx={{ color: '#078DEE', flexShrink: 0 }} />
                                <Typography sx={{ flex: 1, fontSize: '12px', color: '#3c3c43', letterSpacing: '-0.005em', minWidth: 0 }}>
                                    Would you like to reschedule or assign your next task?
                                </Typography>
                                {effectiveUpNextItem?.taskId && practiceId && (
                                    <Box data-testid="reschedule-banner-assignee" sx={{ flexShrink: 0 }}>
                                        <TaskAssigneeHeads
                                            practiceId={practiceId}
                                            taskId={effectiveUpNextItem.taskId}
                                            initialAssignees={effectiveUpNextItem.rawTask?.assignees || []}
                                            leadId={currentLeadId}
                                            onLeadTasksReassigned={() => refresh?.({ bustCache: true })}
                                        />
                                    </Box>
                                )}
                                <Button
                                    data-testid="reschedule-banner-btn"
                                    size="small"
                                    variant="contained"
                                    disableElevation
                                    onClick={() => setBannerRescheduleOpen(true)}
                                    sx={{
                                        fontSize: '11px', py: 0.4, px: 1.25,
                                        borderRadius: '6px', flexShrink: 0,
                                        bgcolor: '#078DEE', color: '#fff',
                                        '&:hover': { bgcolor: '#0671c4' },
                                    }}
                                >
                                    Reschedule
                                </Button>
                                <IconButton
                                    data-testid="reschedule-banner-dismiss"
                                    size="small"
                                    aria-label="Dismiss"
                                    onClick={() => setShowRescheduleBanner(false)}
                                    sx={{ color: '#6e6e73', p: 0.25, flexShrink: 0, '&:hover': { color: '#1d1d1f' } }}
                                >
                                    <Iconify icon="eva:close-fill" width={16} height={16} />
                                </IconButton>
                                <RescheduleDialog
                                    open={bannerRescheduleOpen}
                                    onClose={() => setBannerRescheduleOpen(false)}
                                    onConfirm={async (newDate) => {
                                        const ok = await handleChangeDueDate(effectiveUpNextItem, newDate);
                                        if (ok !== false) setShowRescheduleBanner(false);
                                        return ok;
                                    }}
                                    item={effectiveUpNextItem}
                                />
                            </Box>
                        )}

                        {/* Hide when ActionPanel is open — panel occupies the right column,
                            empty-state card in the shrunken left would look broken. */}
                        {!initialLoading && !allPaused && items.length === 0 && !showCustomReminderToggle && !actionPanelItem && (
                            <TasksEmptyStateCard
                                variant="no-tasks"
                                onCreateReminder={() => setShowCustomReminderToggle(true)}
                                onContactLead={handleContactLead}
                            />
                        )}

                        {!initialLoading && !showCustomReminderToggle && allTasksComplete && (
                            <TasksEmptyStateCard
                                variant="all-complete"
                                onCreateReminder={() => setShowCustomReminderToggle(true)}
                            />
                        )}

                        {!initialLoading && !allPaused && effectiveUpNextItem && (
                            <Box sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ visibility: recentlyCompleted ? 'hidden' : 'visible', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <UpNextCardV2
                                        key={String(effectiveUpNextItem?.taskId ?? effectiveUpNextItem?.id ?? 'hero')}
                                        item={effectiveUpNextItem}
                                        onMarkComplete={handleCardMarkComplete}
                                        onActionClick={handleActionClick}
                                        onSkip={handleSkip}
                                        onPause={handlePause}
                                        onChangeDueDate={handleChangeDueDate}
                                        practiceId={practiceId}
                                        leadEmail={leadEmail}
                                        panelOpen={!!actionPanelItem}
                                        leadId={currentLeadId}
                                        onLeadTasksReassigned={() => refresh?.({ bustCache: true })}
                                    />
                                </Box>
                                {recentlyCompleted && (
                                    <Box data-testid="upnext-ack-window" sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
                                        <RecentlyCompletedCard
                                            item={recentlyCompleted.completed}
                                            label={recentlyCompleted.label}
                                            onDismiss={() => setRecentlyCompleted(null)}
                                            onUndo={handleAckUndo}
                                            fillHeight
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                        {!initialLoading && !allPaused && recentlyCompleted && !effectiveUpNextItem && (
                            <Box data-testid="upnext-ack-window">
                                <RecentlyCompletedCard
                                    item={recentlyCompleted.completed}
                                    label={recentlyCompleted.label}
                                    onDismiss={() => setRecentlyCompleted(null)}
                                    onUndo={handleAckUndo}
                                />
                            </Box>
                        )}
                    </Box>

                    {actionPanelItem && (
                        <Box
                            sx={{
                                flex: '0 0 70%',
                                minWidth: 0,
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                '& > [data-testid="action-panel"]': { flex: '1 1 auto', width: '100%' },
                                '@media (max-width: 900px)': { flex: '0 0 100%' },
                            }}
                        >
                            <ActionPanel
                                // Keyed by item id — forces a remount on item switch so
                                // ActionPanel's mount-only template-resolve effects (and
                                // any panel it routes to, e.g. FollowUpPanel) don't keep
                                // running against a stale, previously-actioned item. See
                                // AttendedGuidedChecklist.jsx for the full rationale.
                                key={actionPanelItem?.id ?? 'none'}
                                item={actionPanelItem}
                                practiceId={practiceId}
                                leadId={currentLeadId}
                                leadDetail={leadDetail}
                                onClose={handlePanelClose}
                                onComplete={handlePanelComplete}
                                handleReloadLeads={onReloadLeads}
                                onOpenSidebarTab={onOpenSidebarTab}
                                callScriptCategory="OUTBOUND"
                            />
                        </Box>
                    )}
                </Box>
                )}

                {showUnpausePrompt && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: '10px',
                            bgcolor: 'rgba(255, 171, 0, 0.08)',
                            border: '1px solid rgba(255, 171, 0, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.5,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Iconify icon="mdi:pause-circle-outline" width={18} sx={{ color: '#FFAB00', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#1d1d1f' }}>
                                {pausedCount} task{pausedCount !== 1 ? 's' : ''} paused — unpause them now?
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Button
                                size="small"
                                variant="contained"
                                disableElevation
                                disabled={unpauseLoading}
                                onClick={() => {
                                    setUnpauseLoading(true);
                                    // treatment-scoped to avoid cross-treatment resume leak
                                    dispatch(resumeLeadCascade(practiceId, currentLeadId, ({ success }) => {
                                        setUnpauseLoading(false);
                                        setShowUnpausePrompt(false);
                                        if (success) {
                                            refresh({ bustCache: true });
                                            broadcastTasksChanged('resume-cascade');
                                        }
                                    }, { leadTreatmentId }));
                                }}
                                sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'none', bgcolor: '#FFAB00', color: '#1d1d1f', borderRadius: '6px', px: 1.5, py: 0.5, '&:hover': { bgcolor: '#e09800' } }}
                            >
                                {unpauseLoading ? 'Unpausing…' : 'Unpause'}
                            </Button>
                            <Button
                                size="small"
                                disabled={unpauseLoading}
                                onClick={() => setShowUnpausePrompt(false)}
                                sx={{ fontSize: '12px', fontWeight: 400, textTransform: 'none', color: '#6e6e73' }}
                            >
                                Keep paused
                            </Button>
                        </Stack>
                    </Box>
                )}

                {!showCustomReminderToggle && !initialLoading && !allPaused && miniGridItems.length > 0 && (
                    <Box sx={{ mt: 3, '@keyframes v2-minigrid-fade': { from: { opacity: 0, transform: 'translateY(-8px)' }, to: { opacity: 1, transform: 'none' } }, animation: 'v2-minigrid-fade 280ms 160ms cubic-bezier(0.32, 0.72, 0, 1) both' }}>
                        <ChecklistMiniGrid
                            groups={miniGridGroups}
                            featuredItem={effectiveUpNextItem}
                            onMarkComplete={handleCardMarkComplete}
                            onUndo={handleCardUndo}
                            onItemClick={handleItemAction}
                            onSkip={handleSkip}
                            onChangeDueDate={handleChangeDueDate}
                        />
                    </Box>
                )}

                {!showCustomReminderToggle && !initialLoading && !allPaused && !allTasksComplete && items.length > 0 && (
                    <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-start' }}>
                        <Button
                            variant="outlined"
                            onClick={() => setShowCustomReminderToggle(true)}
                            startIcon={<Iconify icon="eva:plus-fill" width={18} height={18} />}
                            sx={{
                                color: '#00AB55',
                                borderColor: 'rgba(0,171,85,0.48)',
                                borderRadius: '999px',
                                fontWeight: 600,
                                fontSize: '13px',
                                letterSpacing: '-0.005em',
                                textTransform: 'none',
                                px: 2,
                                py: 0.75,
                                boxShadow: 'none',
                                '&:hover': {
                                    borderColor: '#00AB55',
                                    bgcolor: 'rgba(0,171,85,0.08)',
                                    boxShadow: 'none',
                                },
                                '&:active': { bgcolor: 'rgba(0,171,85,0.16)' },
                            }}
                        >
                            Add custom reminder
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Pause dialog — mirrors AGCv2.jsx:1455-1510 exactly */}
            <Dialog
                data-testid="pause-dialog-v2"
                open={pauseDialogItem != null}
                onClose={() => setPauseDialogItem(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6e6e73', mb: 0.5 }}>
                        Pause task
                    </Typography>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                        {pauseDialogItem?.title || 'Pause task'}
                    </Typography>
                </Box>
                <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
                    <Typography sx={{ fontSize: '13px', color: '#3c3c43', mb: 1.5, letterSpacing: '-0.005em' }}>
                        Resume this task on:
                    </Typography>
                    <input
                        type="datetime-local"
                        value={pauseUntilDate}
                        onChange={(e) => setPauseUntilDate(e.target.value)}
                        style={{
                            width: '100%', padding: '10px 12px', fontSize: '14px',
                            borderRadius: '8px', border: '1.5px solid rgba(0,0,0,0.15)',
                            fontFamily: 'inherit', color: '#1d1d1f', outline: 'none', boxSizing: 'border-box',
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                    <Button
                        onClick={() => setPauseDialogItem(null)}
                        sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '14px', fontWeight: 500 }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        data-testid="pause-confirm-btn-v2"
                        variant="contained"
                        disableElevation
                        disabled={!pauseUntilDate}
                        loading={pauseLoading}
                        onClick={handlePauseConfirm}
                        sx={{ ...CRM_PRIMARY_BUTTON_SX, textTransform: 'none', fontSize: '14px', fontWeight: 600, py: 0.75, px: 2.5, borderRadius: '8px' }}
                    >
                        Pause task
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
}

ScheduleReminderTaskSection.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    /** LeadTreatment PK — scopes pause/resume-cascade to this treatment only. */
    leadTreatmentId: PropTypes.number,
    /** Opens a sidebar tab in the host shell (email/whatsapp/overview). */
    onOpenSidebarTab: PropTypes.func,
    /** Arms/disarms the host's WhatsApp send->complete anchor (FollowUpV2). */
    onArmWhatsappTask: PropTypes.func,
    /** Reloads the leads table after a mutation. */
    onReloadLeads: PropTypes.func,
    /** Host close handler — invoked by ScheduleTaskReminder on successful save. */
    onClose: PropTypes.func,
    /** Optional shared useLeadReminderTasks instance from the host (single source of truth). */
    checklist: PropTypes.object,
    renderHero: PropTypes.bool,
    compact: PropTypes.bool,
    debugSource: PropTypes.string,
    /** Debug outline helpers from the host (attDebug toggle). */
    dbgSx: PropTypes.func,
    DbgTag: PropTypes.elementType,
};

ScheduleReminderTaskSection.defaultProps = {
    practiceId: null,
    leadId: null,
    leadDetail: null,
    leadTreatmentId: null,
    onOpenSidebarTab: null,
    onArmWhatsappTask: null,
    onReloadLeads: null,
    onClose: null,
    checklist: null,
    renderHero: true,
    compact: false,
    debugSource: 'unknown',
    dbgSx: null,
    DbgTag: null,
};
